import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Allows only safe formatting tags used by the rich text editor
 *
 * @param {string} html - HTML content to sanitize
 * @returns {string} Sanitized HTML
 */
export const sanitizeHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Configure DOMPurify to allow safe inline styles for indentation
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName === 'style') {
      // Only allow padding-left style for indentation
      const styleValue = data.attrValue;
      const paddingLeftMatch = styleValue.match(/padding-left:\s*(\d+)px/);

      if (paddingLeftMatch) {
        // Validate the padding value (max 240px = 8 levels of 30px indentation)
        const paddingValue = parseInt(paddingLeftMatch[1], 10);
        if (paddingValue >= 0 && paddingValue <= 240) {
          // Keep only the padding-left value
          data.attrValue = `padding-left: ${paddingValue}px`;
        } else {
          // Remove style if value is out of safe range
          data.keepAttr = false;
        }
      } else {
        // Remove style attribute if it doesn't contain padding-left
        data.keepAttr = false;
      }
    }
  });

  const result = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br',              // Paragraphs and line breaks
      'strong', 'b',          // Bold
      'em', 'i',              // Italic
      'u',                    // Underline
      'ul', 'ol', 'li',       // Lists
      'a'                     // Links
    ],
    ALLOWED_ATTR: [
      'href',                 // Link URLs
      'target',               // Open in new tab
      'rel',                  // Security attributes
      'style'                 // Inline styles (for indentation)
    ],
    ALLOWED_URI_REGEXP: /^(?:https?:)/i, // Only http/https links
    KEEP_CONTENT: true,                    // Keep text even if tags removed
    ADD_ATTR: ['target', 'rel']            // Ensure security attributes
  });

  // Remove the hook after sanitization
  DOMPurify.removeHook('uponSanitizeAttribute');

  return result;
};

/**
 * Strips all HTML tags and returns plain text
 * Used for validation and character counting
 *
 * @param {string} html - HTML content
 * @returns {string} Plain text content
 */
export const stripHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // First sanitize to ensure safe HTML
  const sanitized = sanitizeHTML(html);

  // Create temporary element to extract text
  const temp = document.createElement('div');
  temp.innerHTML = sanitized;

  return temp.textContent || temp.innerText || '';
};

/**
 * Validates if HTML content is effectively empty
 * Checks if stripping HTML and whitespace leaves nothing
 *
 * @param {string} html - HTML content
 * @returns {boolean} True if empty
 */
export const isHTMLEmpty = (html) => {
  return stripHTML(html).trim().length === 0;
};
